function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
var _buyButtonsIntersectionObserver = _classPrivateFieldLooseKey("buyButtonsIntersectionObserver");
var _mainBottomObserver = _classPrivateFieldLooseKey("mainBottomObserver");
var _resetTimeout = _classPrivateFieldLooseKey("resetTimeout");
var _isStuck = _classPrivateFieldLooseKey("isStuck");
var _animationTimeout = _classPrivateFieldLooseKey("animationTimeout");
var _abortController = _classPrivateFieldLooseKey("abortController");
var _targetAddToCartButton = _classPrivateFieldLooseKey("targetAddToCartButton");
var _currentQuantity = _classPrivateFieldLooseKey("currentQuantity");
var _hiddenByBottom = _classPrivateFieldLooseKey("hiddenByBottom");
var _addedFromStickyBar = _classPrivateFieldLooseKey("addedFromStickyBar");
var _setupIntersectionObserver = _classPrivateFieldLooseKey("setupIntersectionObserver");
var _handleVariantUpdate = _classPrivateFieldLooseKey("handleVariantUpdate");
var _handleVariantSelected = _classPrivateFieldLooseKey("handleVariantSelected");
var _handleVariantUnavailable = _classPrivateFieldLooseKey("handleVariantUnavailable");
var _handleCartAddComplete = _classPrivateFieldLooseKey("handleCartAddComplete");
var _handleQuantityUpdate = _classPrivateFieldLooseKey("handleQuantityUpdate");
var _showStickyBar = _classPrivateFieldLooseKey("showStickyBar");
var _hideStickyBar = _classPrivateFieldLooseKey("hideStickyBar");
var _getProductForm = _classPrivateFieldLooseKey("getProductForm");
var _getInitialQuantity = _classPrivateFieldLooseKey("getInitialQuantity");
var _updateButtonText = _classPrivateFieldLooseKey("updateButtonText");
class StickyAddToCartComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Updates the button text to include quantity
     */
    Object.defineProperty(this, _updateButtonText, {
      value: _updateButtonText2
    });
    /**
     * Gets the initial quantity from the data attribute
     */
    Object.defineProperty(this, _getInitialQuantity, {
      value: _getInitialQuantity2
    });
    // Helper methods
    /**
     * Gets the product form element
     * @returns {HTMLElement | null}
     */
    Object.defineProperty(this, _getProductForm, {
      value: _getProductForm2
    });
    /**
     * Hides the sticky bar with animation
     */
    Object.defineProperty(this, _hideStickyBar, {
      value: _hideStickyBar2
    });
    /**
     * Shows the sticky bar with animation
     */
    Object.defineProperty(this, _showStickyBar, {
      value: _showStickyBar2
    });
    /**
     * Sets up the IntersectionObserver to watch the buy buttons visibility
     */
    Object.defineProperty(this, _setupIntersectionObserver, {
      value: _setupIntersectionObserver2
    });
    this.requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];
    /** @type {IntersectionObserver | null} */
    Object.defineProperty(this, _buyButtonsIntersectionObserver, {
      writable: true,
      value: null
    });
    /** @type {IntersectionObserver | null} */
    Object.defineProperty(this, _mainBottomObserver, {
      writable: true,
      value: null
    });
    /** @type {number | undefined} */
    Object.defineProperty(this, _resetTimeout, {
      writable: true,
      value: void 0
    });
    /** @type {boolean} */
    Object.defineProperty(this, _isStuck, {
      writable: true,
      value: false
    });
    /** @type {number | null} */
    Object.defineProperty(this, _animationTimeout, {
      writable: true,
      value: null
    });
    /** @type {AbortController} */
    Object.defineProperty(this, _abortController, {
      writable: true,
      value: new AbortController()
    });
    /** @type {HTMLButtonElement | null} */
    Object.defineProperty(this, _targetAddToCartButton, {
      writable: true,
      value: null
    });
    /** @type {number} */
    Object.defineProperty(this, _currentQuantity, {
      writable: true,
      value: 1
    });
    /** @type {boolean} */
    Object.defineProperty(this, _hiddenByBottom, {
      writable: true,
      value: false
    });
    /** @type {boolean} - Sepete ekleme sticky bar'dan tetiklendi mi */
    Object.defineProperty(this, _addedFromStickyBar, {
      writable: true,
      value: false
    });
    // Public action handlers
    /**
     * Handles the add to cart button click in the sticky bar
     */
    this.handleAddToCartClick = async () => {
      if (!_classPrivateFieldLooseBase(this, _targetAddToCartButton)[_targetAddToCartButton]) return;
      _classPrivateFieldLooseBase(this, _addedFromStickyBar)[_addedFromStickyBar] = true;
      _classPrivateFieldLooseBase(this, _targetAddToCartButton)[_targetAddToCartButton].dataset.puppet = 'true';
      _classPrivateFieldLooseBase(this, _targetAddToCartButton)[_targetAddToCartButton].click();
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      if (this.refs.addToCartButton.dataset.added !== 'true') {
        this.refs.addToCartButton.dataset.added = 'true';
      }
      if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
      if (_classPrivateFieldLooseBase(this, _resetTimeout)[_resetTimeout]) clearTimeout(_classPrivateFieldLooseBase(this, _resetTimeout)[_resetTimeout]);
      const flyToCartElement = /** @type {FlyToCart} */document.createElement('fly-to-cart');
      const sourceStyles = getComputedStyle(this.refs.productImage);
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
      await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
      _classPrivateFieldLooseBase(this, _resetTimeout)[_resetTimeout] = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
    };
    /**
     * Handles variant update events
     * @param {CustomEvent} event - The variant update event
     */
    Object.defineProperty(this, _handleVariantUpdate, {
      writable: true,
      value: event => {
        if (event.detail.data.productId !== this.dataset.productId) return;
        const variant = event.detail.resource;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
        if (!newStickyAddToCart) return;
        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, {
          childrenOnly: true
        });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = _classPrivateFieldLooseBase(this, _getProductForm)[_getProductForm]();
        if (productForm) {
          _classPrivateFieldLooseBase(this, _targetAddToCartButton)[_targetAddToCartButton] = productForm.querySelector('[ref="addToCartButton"]');
        }
        if (variant == null) {
          _classPrivateFieldLooseBase(this, _handleVariantUnavailable)[_handleVariantUnavailable]();
        }
        // Restore the current quantity display if needed
        _classPrivateFieldLooseBase(this, _updateButtonText)[_updateButtonText]();
      }
    });
    /**
     * Handles variant selected events
     * @param {CustomEvent} event - The variant selected event
     */
    Object.defineProperty(this, _handleVariantSelected, {
      writable: true,
      value: event => {
        // The variant update event will follow and handle all updates via morph
        // We just update the dataset here for tracking
        const variantId = event.detail.resource?.id;
        if (!variantId) return;
        this.dataset.currentVariantId = variantId;
      }
    });
    /**
     * Updates the variant title based on selected options when the variant is unavailable
     */
    Object.defineProperty(this, _handleVariantUnavailable, {
      writable: true,
      value: () => {
        this.dataset.currentVariantId = '';
        const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
        const productId = this.dataset.productId;
        const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
        if (!variantTitleElement || !variantPicker) return;
        const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked')).map(option => /** @type {HTMLInputElement} */option.value).filter(value => value !== '').join(' / ');
        if (!selectedOptions) return;
        variantTitleElement.textContent = selectedOptions;
      }
    });
    /**
     * Handles cart add complete (success or error) - resets puppet flag, hides bar on success
     * @param {CustomEvent} event - The cart event
     */
    Object.defineProperty(this, _handleCartAddComplete, {
      writable: true,
      value: event => {
        // Reset the puppet flag after cart operation
        if (_classPrivateFieldLooseBase(this, _targetAddToCartButton)[_targetAddToCartButton]) {
          _classPrivateFieldLooseBase(this, _targetAddToCartButton)[_targetAddToCartButton].dataset.puppet = 'false';
        }
        if (event.type === ThemeEvents.cartError) {
          _classPrivateFieldLooseBase(this, _addedFromStickyBar)[_addedFromStickyBar] = false;
          return;
        }
        if (event.detail?.data?.didError) return;
        // Sepete eklendiğinde sticky bar'ı gizle (sticky bar'dan veya ana formdan eklenmiş olsun)
        const productId = this.dataset.productId;
        const variantId = this.dataset.currentVariantId;
        const eventProductId = event.detail?.data?.productId;
        const eventVariantId = event.detail?.data?.variantId?.toString();
        const eventSourceId = event.detail?.sourceId?.toString();
        const isCurrentProductAdded = eventProductId && eventProductId === productId || eventVariantId && variantId && eventVariantId === variantId.toString() || eventSourceId && variantId && eventSourceId === variantId.toString() || _classPrivateFieldLooseBase(this, _addedFromStickyBar)[_addedFromStickyBar];
        if (event.type === ThemeEvents.cartUpdate && isCurrentProductAdded) {
          _classPrivateFieldLooseBase(this, _addedFromStickyBar)[_addedFromStickyBar] = false;
          _classPrivateFieldLooseBase(this, _hiddenByBottom)[_hiddenByBottom] = true;
          _classPrivateFieldLooseBase(this, _hideStickyBar)[_hideStickyBar]();
        }
      }
    });
    /**
     * Handles quantity selector update events
     * @param {QuantitySelectorUpdateEvent} event - The quantity update event
     */
    Object.defineProperty(this, _handleQuantityUpdate, {
      writable: true,
      value: event => {
        // Only respond to product page quantity selector updates, not cart drawer
        if (event.detail.cartLine) return;
        _classPrivateFieldLooseBase(this, _currentQuantity)[_currentQuantity] = event.detail.quantity;
        _classPrivateFieldLooseBase(this, _updateButtonText)[_updateButtonText]();
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    _classPrivateFieldLooseBase(this, _setupIntersectionObserver)[_setupIntersectionObserver]();
    const {
      signal
    } = _classPrivateFieldLooseBase(this, _abortController)[_abortController];
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, _classPrivateFieldLooseBase(this, _handleVariantUpdate)[_handleVariantUpdate], {
      signal
    });
    target?.addEventListener(ThemeEvents.variantSelected, _classPrivateFieldLooseBase(this, _handleVariantSelected)[_handleVariantSelected], {
      signal
    });
    document.addEventListener(ThemeEvents.cartUpdate, _classPrivateFieldLooseBase(this, _handleCartAddComplete)[_handleCartAddComplete], {
      signal
    });
    document.addEventListener(ThemeEvents.cartError, _classPrivateFieldLooseBase(this, _handleCartAddComplete)[_handleCartAddComplete], {
      signal
    });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, _classPrivateFieldLooseBase(this, _handleQuantityUpdate)[_handleQuantityUpdate], {
      signal
    });
    _classPrivateFieldLooseBase(this, _getInitialQuantity)[_getInitialQuantity]();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _buyButtonsIntersectionObserver)[_buyButtonsIntersectionObserver]?.disconnect();
    _classPrivateFieldLooseBase(this, _mainBottomObserver)[_mainBottomObserver]?.disconnect();
    _classPrivateFieldLooseBase(this, _abortController)[_abortController].abort();
    if (_classPrivateFieldLooseBase(this, _animationTimeout)[_animationTimeout]) {
      clearTimeout(_classPrivateFieldLooseBase(this, _animationTimeout)[_animationTimeout]);
    }
  }
}
function _setupIntersectionObserver2() {
  const productForm = _classPrivateFieldLooseBase(this, _getProductForm)[_getProductForm]();
  if (!productForm) return;
  const buyButtonsBlock = productForm.closest('.buy-buttons-block');
  if (!buyButtonsBlock) return;

  // In themes migrated from 2.0, the footer element doesn't exist
  const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
  if (!footer) return;

  // Observer for buy buttons visibility
  _classPrivateFieldLooseBase(this, _buyButtonsIntersectionObserver)[_buyButtonsIntersectionObserver] = new IntersectionObserver(entries => {
    const [entry] = entries;
    if (!entry) return;

    // Only show sticky bar if buy buttons have been scrolled past (above viewport)
    if (!entry.isIntersecting && !_classPrivateFieldLooseBase(this, _isStuck)[_isStuck]) {
      // Check if the element is above the viewport (scrolled past) or below (not yet reached)
      const rect = entry.target.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top < 0) {
        // Element is above viewport - show sticky bar
        _classPrivateFieldLooseBase(this, _showStickyBar)[_showStickyBar]();
      }
      // If rect.top >= 0, element is below viewport - don't show sticky bar yet
    } else if (entry.isIntersecting && _classPrivateFieldLooseBase(this, _isStuck)[_isStuck]) {
      _classPrivateFieldLooseBase(this, _hiddenByBottom)[_hiddenByBottom] = false;
      _classPrivateFieldLooseBase(this, _hideStickyBar)[_hideStickyBar]();
    }
  });

  // Observer for footer visibility - hides sticky bar at page bottom
  _classPrivateFieldLooseBase(this, _mainBottomObserver)[_mainBottomObserver] = new IntersectionObserver(entries => {
    const [entry] = entries;
    if (!entry) return;
    if (entry.isIntersecting && _classPrivateFieldLooseBase(this, _isStuck)[_isStuck]) {
      _classPrivateFieldLooseBase(this, _hiddenByBottom)[_hiddenByBottom] = true;
      _classPrivateFieldLooseBase(this, _hideStickyBar)[_hideStickyBar]();
    } else if (!entry.isIntersecting && _classPrivateFieldLooseBase(this, _hiddenByBottom)[_hiddenByBottom]) {
      // Footer out of view - check if we should show sticky bar again
      const rect = buyButtonsBlock.getBoundingClientRect();
      // Only show if buy buttons are above the viewport (scrolled past)
      if (rect.bottom < 0 || rect.top < 0) {
        _classPrivateFieldLooseBase(this, _hiddenByBottom)[_hiddenByBottom] = false;
        _classPrivateFieldLooseBase(this, _showStickyBar)[_showStickyBar]();
      }
    }
  }, {
    rootMargin: '200px 0px 0px 0px'
  });
  _classPrivateFieldLooseBase(this, _buyButtonsIntersectionObserver)[_buyButtonsIntersectionObserver].observe(buyButtonsBlock);
  _classPrivateFieldLooseBase(this, _mainBottomObserver)[_mainBottomObserver].observe(footer);
  _classPrivateFieldLooseBase(this, _targetAddToCartButton)[_targetAddToCartButton] = productForm.querySelector('[ref="addToCartButton"]');
}
function _showStickyBar2() {
  const {
    stickyBar
  } = this.refs;
  _classPrivateFieldLooseBase(this, _isStuck)[_isStuck] = true;
  stickyBar.dataset.stuck = 'true';
}
function _hideStickyBar2() {
  const {
    stickyBar
  } = this.refs;
  _classPrivateFieldLooseBase(this, _isStuck)[_isStuck] = false;
  stickyBar.dataset.stuck = 'false';
}
function _getProductForm2() {
  const productId = this.dataset.productId;
  if (!productId) return null;
  const sectionElement = this.closest('.shopify-section');
  if (!sectionElement) return null;
  const sectionId = sectionElement.id.replace('shopify-section-', '');
  return document.querySelector(`#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`);
}
function _getInitialQuantity2() {
  _classPrivateFieldLooseBase(this, _currentQuantity)[_currentQuantity] = parseInt(this.dataset.initialQuantity || '1') || 1;
  _classPrivateFieldLooseBase(this, _updateButtonText)[_updateButtonText]();
}
function _updateButtonText2() {
  const {
    addToCartButton,
    quantityDisplay,
    quantityNumber
  } = this.refs;
  const available = !addToCartButton.disabled;

  // Update the quantity number
  quantityNumber.textContent = _classPrivateFieldLooseBase(this, _currentQuantity)[_currentQuantity].toString();

  // Show/hide the quantity display based on availability and quantity
  if (available && _classPrivateFieldLooseBase(this, _currentQuantity)[_currentQuantity] > 1) {
    quantityDisplay.style.display = 'inline';
  } else {
    quantityDisplay.style.display = 'none';
  }
}
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
